/**
 * Leader Election - Ensures only one tab processes SignalR events
 * Uses localStorage and BroadcastChannel for coordination
 */

import { logger } from '../core/utils/logger'

const LEADER_KEY = 'ordelio-leader'
const HEARTBEAT_INTERVAL = 2000 // 2 seconds
const LEADER_TIMEOUT = 5000 // 5 seconds
const CHANNEL_NAME = 'ordelio-leader-election'

interface LeaderMessage {
  type: 'heartbeat' | 'claim' | 'resign'
  tabId: string
  timestamp: number
}

interface LeaderInfo {
  tabId: string
  timestamp: number
}

class LeaderElection {
  private isLeader = false
  private tabId: string
  private heartbeatInterval: number | null = null
  private checkLeaderInterval: number | null = null
  private channel: BroadcastChannel | null = null
  private leaderChangeListeners: Set<(isLeader: boolean) => void> = new Set()

  constructor() {
    this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  async initialize(): Promise<void> {
    // Initialize BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CHANNEL_NAME)
      this.channel.onmessage = (event: MessageEvent<LeaderMessage>) => {
        this.handleLeaderMessage(event.data)
      }
    }

    // Start leader election
    this.startElection()

    // Handle tab close
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    // Handle page visibility (tab hidden/shown)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    
    logger.debug('[LeaderElection] Initialized')
  }

  cleanup(): void {
    if (this.isLeader) {
      this.resignLeadership()
    }

    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    
    if (this.channel) {
      this.channel.close()
      this.channel = null
    }

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    if (this.checkLeaderInterval) {
      clearInterval(this.checkLeaderInterval)
      this.checkLeaderInterval = null
    }
  }

  private handleBeforeUnload = () => {
    if (this.isLeader) {
      this.resignLeadership()
    }
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // Check if we need to take over leadership
      this.checkLeaderStatus()
    }
  }

  /**
   * Start the leader election process
   */
  private startElection(): void {
    // Check if there's an existing leader
    const existingLeader = this.getLeaderInfo()
    
    if (!existingLeader || Date.now() - existingLeader.timestamp > LEADER_TIMEOUT) {
      // No leader or leader timed out, claim leadership
      this.claimLeadership()
    } else if (existingLeader.tabId === this.tabId) {
      // We were already the leader (page reload)
      this.claimLeadership()
    } else {
      // Another tab is the leader
      logger.info(`[LeaderElection] Tab ${existingLeader.tabId} is the leader`)
      this.isLeader = false
      this.startLeaderCheck()
    }
  }

  /**
   * Claim leadership
   */
  private claimLeadership(): void {
    this.isLeader = true
    this.updateLeaderInfo()
    
    logger.info(`[LeaderElection] Tab ${this.tabId} claimed leadership`)
    
    // Broadcast claim message
    this.broadcastMessage({ type: 'claim', tabId: this.tabId, timestamp: Date.now() })
    
    // Start heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }
    this.heartbeatInterval = window.setInterval(() => {
      this.updateLeaderInfo()
      this.broadcastMessage({ type: 'heartbeat', tabId: this.tabId, timestamp: Date.now() })
    }, HEARTBEAT_INTERVAL)

    // Stop checking for leader
    if (this.checkLeaderInterval) {
      clearInterval(this.checkLeaderInterval)
      this.checkLeaderInterval = null
    }

    // Notify listeners
    this.notifyLeaderChange(true)
  }

  /**
   * Resign leadership
   */
  private resignLeadership(): void {
    if (!this.isLeader) return

    logger.info(`[LeaderElection] Tab ${this.tabId} resigning leadership`)
    
    this.isLeader = false
    
    // Clear heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    // Remove from localStorage
    const leader = this.getLeaderInfo()
    if (leader && leader.tabId === this.tabId) {
      localStorage.removeItem(LEADER_KEY)
    }

    // Broadcast resignation
    this.broadcastMessage({ type: 'resign', tabId: this.tabId, timestamp: Date.now() })

    // Notify listeners
    this.notifyLeaderChange(false)
  }

  /**
   * Start checking for leader status (for follower tabs)
   */
  private startLeaderCheck(): void {
    if (this.checkLeaderInterval) return

    this.checkLeaderInterval = window.setInterval(() => {
      this.checkLeaderStatus()
    }, HEARTBEAT_INTERVAL)
  }

  /**
   * Check if the leader is still alive
   */
  private checkLeaderStatus(): void {
    const leader = this.getLeaderInfo()
    
    if (!leader || Date.now() - leader.timestamp > LEADER_TIMEOUT) {
      logger.info(`[LeaderElection] Leader timeout detected, claiming leadership`)
      this.claimLeadership()
    }
  }

  /**
   * Handle leader messages from other tabs
   */
  private handleLeaderMessage(message: LeaderMessage): void {
    if (message.tabId === this.tabId) return // Ignore our own messages

    switch (message.type) {
      case 'claim':
        // Another tab claimed leadership
        if (this.isLeader && message.timestamp > this.getLeaderInfo()!.timestamp) {
          // They claimed after us, we need to resolve conflict
          // Tab with lower tabId wins (deterministic)
          if (message.tabId < this.tabId) {
            logger.info(`[LeaderElection] Another tab claimed leadership, stepping down`)
            this.resignLeadership()
            this.startLeaderCheck()
          }
        } else if (!this.isLeader) {
          logger.debug(`[LeaderElection] Tab ${message.tabId} is now the leader`)
          this.startLeaderCheck()
        }
        break

      case 'heartbeat':
        // Leader is still alive
        if (!this.isLeader) {
          // Update our knowledge of the leader
          logger.debug(`[LeaderElection] Received heartbeat from leader ${message.tabId}`)
        }
        break

      case 'resign':
        // Leader resigned
        if (!this.isLeader) {
          logger.info(`[LeaderElection] Leader resigned, starting election`)
          // Wait a bit to avoid race conditions
          setTimeout(() => this.checkLeaderStatus(), 100)
        }
        break
    }
  }

  /**
   * Update leader info in localStorage
   */
  private updateLeaderInfo(): void {
    const info: LeaderInfo = {
      tabId: this.tabId,
      timestamp: Date.now()
    }
    localStorage.setItem(LEADER_KEY, JSON.stringify(info))
  }

  /**
   * Get current leader info from localStorage
   */
  private getLeaderInfo(): LeaderInfo | null {
    try {
      const data = localStorage.getItem(LEADER_KEY)
      if (!data) return null
      return JSON.parse(data) as LeaderInfo
    } catch {
      return null
    }
  }

  /**
   * Broadcast message to other tabs
   */
  private broadcastMessage(message: LeaderMessage): void {
    if (!this.channel) return
    
    try {
      this.channel.postMessage(message)
    } catch (error) {
      logger.error('[LeaderElection] Failed to broadcast message:', error)
    }
  }

  /**
   * Check if this tab is the leader
   */
  isLeaderTab(): boolean {
    return this.isLeader
  }

  /**
   * Get this tab's ID
   */
  getTabId(): string {
    return this.tabId
  }

  /**
   * Listen for leader changes
   */
  onLeaderChange(callback: (isLeader: boolean) => void): () => void {
    this.leaderChangeListeners.add(callback)
    // Immediately notify current state
    callback(this.isLeader)
    
    return () => {
      this.leaderChangeListeners.delete(callback)
    }
  }

  /**
   * Notify all listeners of leader change
   */
  private notifyLeaderChange(isLeader: boolean): void {
    this.leaderChangeListeners.forEach(listener => {
      try {
        listener(isLeader)
      } catch (error) {
        logger.error('[LeaderElection] Error in leader change listener:', error)
      }
    })
  }

  /**
   * Cleanup on unmount
   */
  destroy(): void {
    this.resignLeadership()
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }
    
    if (this.checkLeaderInterval) {
      clearInterval(this.checkLeaderInterval)
    }
    
    if (this.channel) {
      this.channel.close()
    }
  }
}

export const leaderElection = new LeaderElection()
